/**
 * Per-salon "recently searched" terms and "recently viewed" product rows,
 * persisted in localStorage. Used by the shop search pane to populate its
 * default (no-query) state. Storage is best-effort: any read/write failure
 * (private mode, quota, SSR) degrades to an empty list rather than throwing.
 */
import { productCardImages } from './productImages';
import { effectiveVariantPrice } from './shopPromotions';

const TERMS_KEY = (slug) => `stylon:recentSearches:${slug || '_'}`;
const VIEWED_KEY = (slug) => `stylon:recentlyViewed:${slug || '_'}`;
const TERMS_CAP = 8;
const VIEWED_CAP = 12;

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(key, arr) {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

/**
 * Compact, render-ready row for a product: the shape the search rows consume.
 * Stored verbatim for recently-viewed snapshots and recomputed live for
 * search/browse results so both render through the same component.
 */
export function productRowModel(product, campaigns) {
  if (!product?.id) return null;
  const variants = product.variants ?? [];
  const priceVariant = variants[0];
  // Through the one display rule, so a search row says what the card and the
  // checkout say. Called WITHOUT campaigns for the copy kept in storage (the
  // recently-viewed list): a remembered row holds the stored price, and the
  // sale is applied when it is drawn, never frozen in from the day it was seen.
  const shown = effectiveVariantPrice(priceVariant, product, campaigns);
  const price = shown.price;
  const compareAt = shown.discounted ? shown.compareAt : null;
  // The one card rule — the variations' photographs, then the product's own.
  // A search row that read the variation table alone showed no picture on a
  // catalogue that keeps its photographs on the product.
  const image = productCardImages(product, variants)[0]?.url ?? null;

  const stockTracked = !!product?.trackInventory;
  const total = stockTracked
    ? variants.reduce((acc, v) => acc + Number(v.stockOnHand ?? 0), 0)
    : Infinity;
  let status = 'in';
  if (stockTracked && total <= 0) status = 'out';
  else if (stockTracked && total <= 2) status = 'low';

  return { id: product.id, name: product.name, image, price, compareAt, status };
}

// ── Recent search terms ─────────────────────────────────────────────
export function getRecentSearches(slug) {
  return read(TERMS_KEY(slug));
}

export function addRecentSearch(slug, term) {
  const value = String(term || '').trim();
  if (!value) return getRecentSearches(slug);
  const next = [
    value,
    ...getRecentSearches(slug).filter((x) => x.toLowerCase() !== value.toLowerCase()),
  ].slice(0, TERMS_CAP);
  write(TERMS_KEY(slug), next);
  return next;
}

export function clearRecentSearches(slug) {
  write(TERMS_KEY(slug), []);
  return [];
}

// ── Recently viewed products ────────────────────────────────────────
export function getRecentlyViewed(slug) {
  return read(VIEWED_KEY(slug));
}

export function addRecentlyViewed(slug, product) {
  const row = productRowModel(product);
  if (!row) return getRecentlyViewed(slug);
  const next = [row, ...getRecentlyViewed(slug).filter((x) => x.id !== row.id)].slice(0, VIEWED_CAP);
  write(VIEWED_KEY(slug), next);
  return next;
}

export function clearRecentlyViewed(slug) {
  write(VIEWED_KEY(slug), []);
  return [];
}
